import {
    action,
    comparer,
    computed,
    makeObservable,
    observable,
    reaction,
    runInAction,
} from "mobx";
import { BaseStore } from "stores/BaseStore";
import { AcxStore } from "stores/RootStore";
import type { IRootStore } from "stores/RootStore";
import { AuthStatus, AuthStore } from "stores/AuthStore";
import ClassifierService from "services/ClassifierService";
import ModuleService from "components/Admin/Organizations/OrganizationDetail/OrganizationModules/Module.service";
import {
    Module,
    Question,
} from "components/Admin/Organizations/types/Module.type";
import type Classifier from "models/ClassifierModel";
import type { ExtendedMetadataClassifier } from "models/ClassifierModel";
import type ClassifierGroup from "models/ClassifierGroup";
import type ClassifierCategory from "models/ClassifierCategoryId";
import type { UnMappedCustomerType } from "models/CustomerType";
import CustomerTypesService from "services/CustomerTypesService";
import LLMClassifierService from "services/LLMClassifierService";
import type {
    SlimExtractionLLMClassifierResponse,
    SlimLLMClassifierResponse,
} from "models/LLMs";
import {
    SignalsThemes,
    SignalsTopics,
    type SignalsGroupByCatalogItem,
    type SignalsOrderRuleCatalogItem,
} from "components/Signals/SignalsModels";
import { ApplicationFiltersStore } from "./ApplicationFilters/ApplicationFiltersStore";
import type {
    ContactTypeOption,
    PrimaryDriver,
    PrimaryTopic,
} from "./ApplicationFilters/ApplicationFiltersStore";
import { HipaaCompliance } from "./ApplicationFilters/Filters/HipaaCompliance";
import type { SmallCardFormattedData } from "components/Signals/BaseObjects/SmallCard/SmallCardStore";
import { formatSmallCardResponse } from "components/Signals/BaseObjects/utils";
import { SignalsService } from "services/SignalsService";

type HipaaOption = {
    id: string;
    label: string;
    value: HipaaCompliance;
};

const DEFAULT_HIPAA_OPTIONS: HipaaOption[] = [
    {
        id: "Compliant",
        label: "HIPAA Compliant",
        value: HipaaCompliance.Compliant,
    },
    {
        id: "Not Compliant",
        label: "Not HIPAA Compliant",
        value: HipaaCompliance.NotCompliant,
    },
    {
        id: "Not Applicable",
        label: "N/A",
        value: HipaaCompliance.NotApplicable,
    },
];

const DEFAULT_SELECTION_LIMIT = 7;

@AcxStore
class OrgOptionsStore extends BaseStore {
    private authStore: AuthStore;
    private applicationFiltersStore: ApplicationFiltersStore;
    private classifierService: ClassifierService = new ClassifierService();
    private signalsService: SignalsService = new SignalsService();
    private moduleService = ModuleService;
    private customerTypesService: CustomerTypesService =
        new CustomerTypesService();
    private llmClassifierService: LLMClassifierService =
        new LLMClassifierService();
    private hasLoadedOptions: boolean = false;
    private currentOrgId?: string;
    private optionRefreshPromises = new Map<string, Promise<void>>();

    @observable userClassifiers: Classifier[] = [];
    @observable classifierCategories: ClassifierCategory[] = [];
    @observable orgModules: Module[] = [];
    @observable orgScoredModules: Module[] = [];
    @observable defaultModuleId?: string;
    @observable defaultClassifierIds: string[] = [];
    @observable defaultClassifierCategory?: ClassifierCategory;
    @observable customerTypes: UnMappedCustomerType[] = [];
    @observable llmClassifiers: SlimLLMClassifierResponse[] = [];
    @observable
    extractionLlmClassifiers: SlimExtractionLLMClassifierResponse[] = [];
    @observable
    extendedMetadataObjectClassifiers: ExtendedMetadataClassifier[] = [];
    @observable topics: SignalsTopics[] = [];
    @observable themes: SignalsThemes[] = [];
    @observable primaryTopics: PrimaryTopic[] = [];
    @observable primaryDrivers: PrimaryDriver[] = [];
    @observable signalsGroupByCatalog: SignalsGroupByCatalogItem[] = [];
    // Distinguishes "the catalog says there are no group-by options" from "the catalog has not
    // arrived yet". Grouped charts treat an option missing from the catalog as unavailable, so
    // they must not read it before it loads.
    @observable signalsGroupByCatalogLoaded = false;
    @observable signalsOrderRuleCatalog: SignalsOrderRuleCatalogItem[] = [];
    @observable hipaaOptions: HipaaOption[] = [];
    @observable totalConversations?: SmallCardFormattedData;

    constructor(rootStore: IRootStore) {
        super("OptionsStore");
        makeObservable(this);

        this.authStore = rootStore.getStore(AuthStore);
        this.applicationFiltersStore = rootStore.getStore(
            ApplicationFiltersStore,
        );

        const shouldLoad =
            !this.hasLoadedOptions ||
            this.currentOrgId !==
                this.authStore.orgStore?.selectedOrganization?.id;

        reaction(
            () => this.applicationFiltersStore.toRequestObject(),
            () => {
                void this.refreshTotalConversations();
            },
            {
                fireImmediately: true,
                equals: comparer.structural,
            },
        );

        reaction(
            () => ({
                effectiveOrgId: this.authStore.effectiveOrgId,
                effectiveUserId: this.authStore.effectiveUserId,
                selectedOrgId: this.authStore.orgStore?.selectedOrganization?.id,
                status: this.authStore.status,
            }),
            async (currentScope, previousScope) => {
                if (
                    currentScope.status !== AuthStatus.Initialized ||
                    !previousScope
                ) {
                    return;
                }

                const scopeChanged =
                    currentScope.effectiveOrgId !==
                        previousScope.effectiveOrgId ||
                    currentScope.effectiveUserId !==
                        previousScope.effectiveUserId ||
                    currentScope.selectedOrgId !== previousScope.selectedOrgId;

                if (!scopeChanged) {
                    return;
                }

                this.resetOptionsState();
                await this.refreshAllOptions(true);
            },
        );

        if (shouldLoad) {
            void this.refreshAllOptions();
        }
    }

    @action
    private resetOptionsState() {
        this.hasLoadedOptions = false;
        this.currentOrgId = undefined;
        this.optionRefreshPromises.clear();
        this.userClassifiers = [];
        this.classifierCategories = [];
        this.orgModules = [];
        this.orgScoredModules = [];
        this.defaultModuleId = undefined;
        this.defaultClassifierIds = [];
        this.defaultClassifierCategory = undefined;
        this.customerTypes = [];
        this.llmClassifiers = [];
        this.extractionLlmClassifiers = [];
        this.extendedMetadataObjectClassifiers = [];
        this.topics = [];
        this.primaryTopics = [];
        this.primaryDrivers = [];
        this.hipaaOptions = [];
        this.totalConversations = undefined;
        // The catalog itself is left in place so grouped charts keep rendering while the new
        // scope's catalog loads, but callers that need it must wait for the reload.
        this.signalsGroupByCatalogLoaded = false;
    }

    @computed get optionsAreLoading() {
        return (
            this.getTaskLoading("options:load user classifiers") ||
            this.getTaskLoading("options:load classifier categories") ||
            this.getTaskLoading("options:load org modules") ||
            this.getTaskLoading("options:load llm classifiers") ||
            this.getTaskLoading(
                "options:load slim extraction llm classifiers",
            ) ||
            this.getTaskLoading(
                "options:load extended metadata object classifiers",
            ) ||
            this.getTaskLoading("options:load customer types") ||
            this.getTaskLoading("options:load topics") ||
            this.getTaskLoading("options:load primary topics") ||
            this.getTaskLoading("options:load primary drivers") ||
            this.getTaskLoading("options:load group by catalog") ||
            this.getTaskLoading("options:load order rule catalog") ||
            this.getTaskLoading("options:load hipaa options") ||
            this.getTaskLoading("options:load total conversations")
        );
    }

    @action
    async refreshAllOptions(force: boolean = false) {
        const orgId = this.authStore.orgStore?.selectedOrganization?.id;
        if (!orgId) return;
        if (!force && this.hasLoadedOptions && this.currentOrgId === orgId) {
            return;
        }

        await Promise.all([
            this.refreshClassifiers(orgId),
            this.refreshModules(orgId),
            this.refreshLLMClassifiers(),
            this.refreshSlimExtractionLLMClassifiers(),
            this.refreshExtendedMetadataObjectClassifiers(),
            this.refreshCustomerTypes(),
            this.refreshTopics(),
            this.refreshThemes(),
            this.refreshPrimaryTopics(),
            this.refreshPrimaryDrivers(),
            this.refreshSignalsGroupByCatalog(),
            this.refreshSignalsOrderRuleCatalog(),
            this.refreshHipaaOptions(),
            this.refreshTotalConversations(),
        ]);

        runInAction(() => {
            this.hasLoadedOptions = true;
            this.currentOrgId = orgId;
        });
    }

    @action
    async refreshClassifiers(orgId?: string) {
        const resolvedOrgId =
            orgId ?? this.authStore.orgStore?.selectedOrganization?.id;

        if (!resolvedOrgId) return;

        return this.runDedupedOptionRefresh(
            `classifiers:${resolvedOrgId}`,
            async () => {
                await Promise.all([
                    this.loadUserClassifiers(resolvedOrgId),
                    this.loadClassifierCategories(),
                ]);
            },
        );
    }

    @action
    async refreshModules(orgId?: string) {
        const resolvedOrgId =
            orgId ?? this.authStore.orgStore?.selectedOrganization?.id;

        if (!resolvedOrgId) return;

        return this.runDedupedOptionRefresh(
            `modules:${resolvedOrgId}`,
            async () => {
                await this.loadOrgModules(resolvedOrgId);
            },
        );
    }

    @action
    async refreshLLMClassifiers() {
        return this.runDedupedOptionRefresh("llm-classifiers", async () => {
            await this.loadLLMClassifiers();
        });
    }

    @action
    async refreshSlimExtractionLLMClassifiers() {
        await this.loadExtractionLLMClassifiers();
    }

    @action
    async refreshExtendedMetadataObjectClassifiers() {
        await this.loadExtendedMetadataObjectClassifiers();
    }

    @action
    async refreshCustomerTypes() {
        return this.runDedupedOptionRefresh("customer-types", async () => {
            await this.loadCustomerTypes();
        });
    }

    @action
    async refreshTopics() {
        return this.runDedupedOptionRefresh("topics", async () => {
            await this.loadTopics();
        });
    }

    @action
    async refreshThemes() {
        return this.runDedupedOptionRefresh("themes", async () => {
            await this.loadThemes();
        });
    }

    @action
    async refreshPrimaryTopics() {
        return this.runDedupedOptionRefresh("primary-topics", async () => {
            await this.loadPrimaryTopics();
        });
    }

    @action
    async refreshPrimaryDrivers() {
        return this.runDedupedOptionRefresh("primary-drivers", async () => {
            await this.loadPrimaryDrivers();
        });
    }

    @action
    async refreshSignalsGroupByCatalog() {
        return this.runDedupedOptionRefresh(
            "signals-group-by-catalog",
            async () => {
                await this.loadSignalsGroupByCatalog();
            },
        );
    }

    @action
    async refreshSignalsOrderRuleCatalog() {
        return this.runDedupedOptionRefresh(
            "signals-order-rule-catalog",
            async () => {
                await this.loadSignalsOrderRuleCatalog();
            },
        );
    }

    private runDedupedOptionRefresh(
        key: string,
        refresh: () => Promise<unknown>,
    ): Promise<void> {
        const existing = this.optionRefreshPromises.get(key);
        if (existing) {
            return existing;
        }

        const next = Promise.resolve(refresh())
            .then(() => undefined)
            .finally(() => {
                if (this.optionRefreshPromises.get(key) === next) {
                    this.optionRefreshPromises.delete(key);
                }
            });

        this.optionRefreshPromises.set(key, next);
        return next;
    }

    private async loadUserClassifiers(orgId: string) {
        return this.setupAsyncTask(
            "options:load user classifiers",
            async () => {
                const res = await this.classifierService.getUserClassifiers(
                    orgId,
                    true,
                );

                runInAction(() => {
                    this.userClassifiers = res.sort((a, b) =>
                        a.name.localeCompare(b.name),
                    );
                    this.updateDefaultClassifiers();
                });
            },
        );
    }

    @computed
    get userClassifiersLoading() {
        return (
            this.getTaskLoading("options:load user classifiers") &&
            !this.userClassifiers.length
        );
    }

    private async loadClassifierCategories() {
        return this.setupAsyncTask(
            "options:load classifier categories",
            async () => {
                const categories =
                    await this.classifierService.getClassifierCategories();

                categories.push({
                    name: "All Classifiers",
                    id: "all-classifiers",
                } as ClassifierGroup);

                runInAction(() => {
                    this.classifierCategories = categories;
                    this.updateDefaultClassifiers();
                });
            },
        );
    }

    @computed
    get classifierCategoriesLoading() {
        return (
            this.getTaskLoading("options:load classifier categories") &&
            !this.classifierCategories.length
        );
    }

    private updateDefaultClassifiers() {
        if (!this.userClassifiers.length || !this.classifierCategories.length)
            return;

        const defaultCategoryName = "Topics";

        const topicsCategory = this.classifierCategories.find(
            (c) => c?.name === defaultCategoryName,
        );

        this.defaultClassifierCategory = topicsCategory;

        const defaultIds = this.userClassifiers.map((c) => c.id);
        const topicIds = this.userClassifiers
            .filter((c) => c.classifierCategory?.name === defaultCategoryName)
            .map((c) => c.id)
            .slice(0, DEFAULT_SELECTION_LIMIT);

        this.defaultClassifierIds = topicIds.length > 0 ? topicIds : defaultIds;
    }

    private async loadOrgModules(orgId: string) {
        return this.setupAsyncTask("options:load org modules", async () => {
            const [allModules, scoredModules] = await Promise.all([
                this.moduleService.getModules(orgId),
                this.signalsService.getLicensedModulesWithScoredQuestions(),
            ]);
            runInAction(() => {
                this.orgModules = allModules
                    .filter((m) => m.isActive)
                    .map(this.mapModuleDataForSignals);

                this.orgScoredModules = scoredModules
                    .filter((m) => m.isActive)
                    .map(this.mapModuleDataForSignals);

                this.setDefaultModuleId();
            });
        });
    }

    private flattenQuestions(questions?: Question[]): Question[] {
        if (!questions?.length) return [];

        return questions.flatMap((question) => [
            question,
            ...this.flattenQuestions(question.questions),
        ]);
    }

    public getQuestionByModuleAndQuestionId(
        moduleId?: string,
        questionId?: string,
    ): Question | undefined {
        if (!moduleId || !questionId) return undefined;

        const module = this.orgModules.find((item) => item.id === moduleId);
        if (!module?.questions?.length) return undefined;

        return this.flattenQuestions(module.questions).find(
            (question) => `${question.id ?? ""}` === questionId,
        );
    }

    @computed
    get orgModulesLoading() {
        return (
            this.getTaskLoading("options:load org modules") &&
            !this.orgModules.length &&
            !this.orgScoredModules.length
        );
    }

    private mapModuleDataForSignals = (module: Module): Module => {
        const descriptionText = module.description
            ? " - " + module.description
            : "";

        const displayName = module.name + descriptionText;

        const mappedQuestions: Question[] =
            module.questions?.flatMap((question) => {
                if (question.questions?.length) {
                    return question.questions.map((childQuestion) => ({
                        ...childQuestion,
                        displayName: `${question.shortQuestionText} - ${childQuestion.shortQuestionText}`,
                    }));
                }

                return [
                    {
                        ...question,
                        displayName: question.shortQuestionText,
                    },
                ];
            }) ?? [];

        const filteredQuestions = mappedQuestions.filter(
            (q) => q.isActive && q.tags?.length,
        );

        return {
            ...module,
            displayName,
            questions: filteredQuestions,
        };
    };

    @action
    private setDefaultModuleId() {
        const qualityModuleId = this.orgModules.find(
            (item) => item.name === "Quality",
        )?.id;

        if (qualityModuleId) {
            this.defaultModuleId = qualityModuleId;
            return;
        }

        const sortedModules = [...this.orgModules].sort((a, b) =>
            a.name.localeCompare(b.name),
        );
        this.defaultModuleId = sortedModules[0]?.id;
    }

    private async loadCustomerTypes() {
        return this.setupAsyncTask("options:load customer types", async () => {
            const customerTypes =
                await this.customerTypesService.getMappedCustomerTypes();

            runInAction(() => {
                this.customerTypes = customerTypes;
            });
        });
    }

    @computed
    get customerTypesLoading() {
        return (
            this.getTaskLoading("options:load customer types") &&
            !this.customerTypes.length
        );
    }

    private async loadLLMClassifiers() {
        return this.setupAsyncTask("options:load llm classifiers", async () => {
            const result =
                await this.llmClassifierService.getSlimLLMClassifiers();

            runInAction(() => {
                this.llmClassifiers = result;
            });
        });
    }

    @computed
    get llmClassifiersLoading() {
        return (
            this.getTaskLoading("options:load llm classifiers") &&
            !this.llmClassifiers.length
        );
    }

    private async loadExtractionLLMClassifiers() {
        return this.setupAsyncTask(
            "options:load slim extraction llm classifiers",
            async () => {
                const result =
                    await this.llmClassifierService.getSlimExtractionLLMClassifiers();

                runInAction(() => {
                    this.extractionLlmClassifiers = result;
                });
            },
        );
    }

    @computed
    get extractionLlmClassifiersLoading() {
        return (
            this.getTaskLoading(
                "options:load slim extraction llm classifiers",
            ) && !this.extractionLlmClassifiers.length
        );
    }

    private async loadExtendedMetadataObjectClassifiers() {
        return this.setupAsyncTask(
            "options:load extended metadata object classifiers",
            async () => {
                const result =
                    await this.classifierService.getExtendedMetadataObjectClassifiers();

                runInAction(() => {
                    this.extendedMetadataObjectClassifiers = result;
                });
            },
        );
    }

    @computed
    get extendedMetadataObjectClassifiersLoading() {
        return (
            this.getTaskLoading(
                "options:load extended metadata object classifiers",
            ) && !this.extendedMetadataObjectClassifiers.length
        );
    }

    private async loadTopics() {
        return this.setupAsyncTask("options:load topics", async () => {
            const result = await this.signalsService.getTopics();

            runInAction(() => {
                this.topics = (result ?? []).sort((a, b) =>
                    (a.topicLabel ?? "").localeCompare(b.topicLabel ?? ""),
                );
            });
        });
    }

    private async loadThemes() {
        if (!this.authStore.canUserView("Drivers")) return;

        return this.setupAsyncTask("options:load themes", async () => {
            const result = await this.signalsService.getThemes();

            runInAction(() => {
                this.themes = (result ?? []).sort((a, b) =>
                    (a.themeLabel ?? "").localeCompare(b.themeLabel ?? ""),
                );
            });
        });
    }

    private async loadPrimaryTopics() {
        if (!this.authStore.canUserView("Topics")) return;

        return this.setupAsyncTask("options:load primary topics", async () => {
            const result = await this.signalsService.getPrimaryTopics();

            runInAction(() => {
                this.primaryTopics = result ?? [];
            });
        });
    }

    private async loadPrimaryDrivers() {
        if (!this.authStore.canUserView("Drivers")) return;

        return this.setupAsyncTask("options:load primary drivers", async () => {
            const result = await this.signalsService.getPrimaryDrivers();

            runInAction(() => {
                this.primaryDrivers = result ?? [];
            });
        });
    }

    @computed
    get topicsLoading() {
        return (
            this.getTaskLoading("options:load topics") && !this.topics.length
        );
    }

    @computed
    get themesLoading() {
        return (
            this.getTaskLoading("options:load themes") && !this.themes.length
        );
    }

    @computed
    get hipaaOptionsLoading() {
        return (
            this.getTaskLoading("options:load hipaa options") &&
            !this.hipaaOptions.length
        );
    }

    @computed
    get totalConversationsLoading() {
        return this.getTaskLoading("options:load total conversations");
    }

    @computed
    get signalsGroupByCatalogLoading() {
        return (
            this.getTaskLoading("options:load group by catalog") &&
            !this.signalsGroupByCatalog.length
        );
    }

    @computed
    get signalsOrderRuleCatalogLoading() {
        return (
            this.getTaskLoading("options:load order rule catalog") &&
            !this.signalsOrderRuleCatalog.length
        );
    }

    @computed
    get hipaaValues() {
        return this.hipaaOptions.map((option) => option.id);
    }

    @computed
    get customerTypeOptions(): ContactTypeOption[] {
        var mapped = this.customerTypes.filter(ct => !!ct.sourceName);
        return mapped.map((ct) => ({ value: ct.sourceName, label: ct.name }) as ContactTypeOption)
    }

    private async loadSignalsGroupByCatalog() {
        return this.setupAsyncTask("options:load group by catalog", async () => {
            const result = await this.signalsService.getGroupByCatalog();

            runInAction(() => {
                // Keep a stable display order in the configure drawer regardless of
                // backend insertion order.
                this.signalsGroupByCatalog = [...(result ?? [])].sort((a, b) =>
                    a.groupByOption.localeCompare(b.groupByOption),
                );
                this.signalsGroupByCatalogLoaded = true;
            });
        });
    }

    /**
     * Resolves once the group-by catalog has loaded at least once for the current scope. Returns
     * immediately after that, so only the first refresh of a session actually waits. A failed load
     * leaves the flag false so the next caller retries.
     *
     * Throws when the load did not succeed, rather than resolving anyway: setupAsyncTask swallows
     * the underlying error into an AsyncTaskStatus.Error this method's caller never sees, so without
     * this check a failed fetch would look identical to a successful one -- the catalog would stay
     * empty and GroupingStore.activeGrouping would read that the same way it reads a genuinely
     * empty org, silently treating every configured chart as ungrouped for this refresh instead of
     * failing it.
     */
    async ensureSignalsGroupByCatalogLoaded(): Promise<void> {
        if (this.signalsGroupByCatalogLoaded) return;

        await this.refreshSignalsGroupByCatalog();

        if (!this.signalsGroupByCatalogLoaded) {
            throw new Error("Failed to load the group-by catalog.");
        }
    }

    private async loadSignalsOrderRuleCatalog() {
        return this.setupAsyncTask(
            "options:load order rule catalog",
            async () => {
                const result = await this.signalsService.getOrderRuleCatalog();

                runInAction(() => {
                    // Preserve backend order for order-rule catalog because identifiers
                    // are matched directly and no UI sort is required here.
                    this.signalsOrderRuleCatalog = [...(result ?? [])];
                });
            },
        );
    }

    @computed
    get totalConversationsValue() {
        return this.totalConversations?.unformattedStat;
    }

    @action
    async refreshHipaaOptions() {
        return this.runDedupedOptionRefresh("hipaa-options", async () => {
            await this.setupAsyncTask("options:load hipaa options", async () => {
                runInAction(() => {
                    this.hipaaOptions = DEFAULT_HIPAA_OPTIONS;
                });
            });
        });
    }

    @action
    async refreshTotalConversations() {
        const filters = this.applicationFiltersStore?.toRequestObject();

        if (!filters) return;

        return this.setupAsyncTask(
            "options:load total conversations",
            async () => {
                const result = await this.signalsService.getSmallCardData(
                    filters,
                    "TotalConversations",
                );

                const formatted = formatSmallCardResponse(result);

                runInAction(() => {
                    this.totalConversations = formatted;
                });
            },
        );
    }
}

export default OrgOptionsStore;
